/* eslint-disable react/jsx-props-no-spreading */
import { useEffect, useMemo, useState } from "react";
import dayjs from "@shared/dayjs";
import { useDispatch } from "react-redux";
import Dropdown from "@shared/v2/Dropdown";
import { headline2 } from "@shared/styles";
import FieldTextV1 from "@shared/UtilsV1/FieldTextV1";
import FieldRadioV1 from "@shared/UtilsV1/FieldRadioV1";
import FieldDateV1 from "@shared/UtilsV1/FieldDateV1";
import FieldSearchLabelsV1 from "@shared/UtilsV1/FieldSearchLabelsV1";
import FieldSelectLabelsWithCheckboxOptions from "@shared/UtilsV1/FieldSelectLabelsWithCheckboxOptions";
import FieldEmailV1 from "@shared/UtilsV1/FieldEmailV1";
import FieldLabelV1 from "@shared/UtilsV1/FieldLabelV1";
import Button from "@shared/v2/Button";
import Toggle from "@shared/v2/Toggle";
import FieldTextAreaV1 from "@shared/UtilsV1/FieldTextAreaV1";
import Tooltip from "@shared/v2/Tooltip";
import { CircleInfoSolidV6, Plus } from "@shared/v2/Icomoon";
import AddMLSSection from "../ModalSections/AddMLSSection";
import AddSponsorModal from "./AddSponsorModal";
import AddRevenuePartnerModal from "./AddRevenuePartnerModal";
import CertificationsAndDesignationsSection from "../ModalSections/CertificationsAndDesignationsSection";
import { useGetServiceAreaOptions } from "../../Settings/Users/helper/hookHelper";
import { containerView } from "./styles";
import { useCurrentUser, useTheme } from "../../reducers/layoutReducer/selectors";
import { setPersonValue } from "../../reducers/contactsReducer";
import { useContactFormOptions, useContactPerson } from "../../reducers/contactsReducer/selectors";
import { inputDescription } from "./styles";
import LicenseNumber from "../ModalSections/LicenseNumber";
import { useEditPersonId } from "../../reducers/userSettingsReducer";

const REV_TYPE_MAP_SRC = {
  "Search PLACE users": "Search PLACE users",
  "Pre-PLACE": "Pre-PLACE",
  "No revenue partner": "No Revenue Partner in Agent Agreement",
};

const reshapeDropdownValue = (value) => ({ label: value, value });

function UserDetailsView() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const currentUser = useCurrentUser();
  const editable = useEditPersonId();
  const person = useContactPerson();
  const formOptions = useContactFormOptions();
  const [isAddingSponsor, setIsAddingSponsor] = useState(false);
  const [isAddingRevenuePartner, setIsAddingRevenuePartner] = useState(false);
  const [serviceAreaOptions, setServiceAreaOptions] = useState([]);
  const [serviceAreaInput, setServiceAreaInput] = useState("");
  const [fluentLanguageInput, setFluentLanguageInput] = useState("");

  const {
    agentHoursOptions,
    brokerageOptions,
    fluenciesOptions,
    positionOptions: basePositionOptions,
    specializationOptions,
  } = formOptions;

  const isOperator = person.displayedPosition === "Operator";

  const positionOptions = useMemo(() => {
    let fixedBasePositions = basePositionOptions;

    if (!currentUser.account?.isAdmin) {
      fixedBasePositions = basePositionOptions.filter(
        (item) => item.label !== "Director of Sales" && item.value !== "Director of Sales",
      );
    }

    if (isOperator) return [...(fixedBasePositions || []), { label: "Operator", value: "Operator" }];
    return fixedBasePositions;
  }, [isOperator, basePositionOptions, currentUser]);

  const isPlaceBrand = theme.name === "place-theme";

  const { licensed_since: licensedSince, organization_start_date: organizationStartDate } =
    person.milestones || {};
  const { active, role, lastSeen } = person.loginUserAttributes || {};
  const roleOptions = useMemo(
    () =>
      role === "Account Owner"
        ? [{ label: "Account Owner", value: "Account Owner" }]
        : [
            { label: "Agent", value: "Agent" },
            { label: "Administrator", value: "Administrator" },
          ],
    [role],
  );

  useGetServiceAreaOptions(serviceAreaInput, setServiceAreaOptions);

  const setPosition = () => {
    if (isOperator) return reshapeDropdownValue(person.displayedPosition);

    return person.position ? reshapeDropdownValue(person.position) : null;
  };

  useEffect(() => {
    if (person.individualRealEstateLlcName) {
      dispatch(setPersonValue({ key: "isRealEstateLlc", value: true }));
    }
  }, []);

  return (
    <div className={containerView}>
      <div className={`tw-text-neutral-gray-75 ${headline2}`}>User Details</div>

      {!active && (
        <>
          <div>This user has been archived</div>
          <FieldDateV1
            label="Last Day"
            value={lastSeen}
            onChange={(value) => dispatch(setPersonValue({ key: "loginUserAttributes.lastSeen", value }))}
            required
          />
          <FieldTextAreaV1
            label="Reason For Leaving"
            value={person.reasonForLeaving}
            onChange={(value) => dispatch(setPersonValue({ key: "reasonForLeaving", value }))}
          />
        </>
      )}
      <Dropdown
        label={
          <span>
            User Role
            <Tooltip
              className="tw-ml-[4px]"
              trigger={<CircleInfoSolidV6 />}
              content={
                <div className="tw-text-left tw-text-12d">
                  <div>
                    <b>User Types</b> define each team member’s permissions within the account.
                  </div>
                  <div>
                    <b>Administrators</b> and the <b>Account Owner</b> have access to everything.
                  </div>
                  <div>
                    <b>Agents</b> cannot change settings or edit the account <i>and</i>
                  </div>
                  <div>can only view listings and people which they are granted access to.</div>
                </div>
              }
            />
          </span>
        }
        options={roleOptions}
        value={roleOptions.find((o) => o.value === role)}
        onChange={(o) => dispatch(setPersonValue({ key: "loginUserAttributes.role", value: o.value }))}
        isDisabled={!currentUser.account.isAdmin || role === "Account Owner"}
        menuShouldComeToFront
      />
      <Dropdown
        label="Position"
        isRequired
        placeholder="Select your position in the company"
        options={positionOptions}
        value={setPosition()}
        error={!setPosition() && "This field is required"}
        onChange={({ value }) => dispatch(setPersonValue({ key: "position", value }))}
        data-cy="select_position"
        disabled={isOperator}
        menuShouldComeToFront
      />
      {isPlaceBrand && (
        <div className={`${inputDescription} tw-tracking-[-.3px] !tw-mt-[-20px]`}>
          Position controls access to tools such as Our PLACE and PLACE Academy. Operations Administrator
          grants higher access and visibility.
        </div>
      )}
      <Dropdown
        label="Production Status"
        error={!person.agentStatus && "This field is required"}
        options={[
          { label: "Licensed - Active Agent", value: "Licensed - Active Agent" },
          { label: "Licensed - Inactive Agent", value: "Licensed - Inactive Agent" },
          { label: "Unlicensed", value: "Unlicensed" },
        ]}
        placeholder="Select production status"
        isRequired
        value={person.agentStatus && reshapeDropdownValue(person.agentStatus)}
        toolTipPlacement="right"
        toolTipInfoContent={
          <div className="tw-text-left tw-text-12d tw-w-[512px]">
            <div>
              <b>Unlicensed:</b> User is not a licensed real estate agent.
            </div>
            <div>
              <b>Licensed - Inactive Agent:</b> User is a licensed real estate agent but does not perform
              transactions.
            </div>
            <div>
              <b>Licensed - Active Agent:</b> User is a licensed real estate agent and actively performs
              transactions.{" "}
              {isPlaceBrand && (
                <span>These users will count towards team’s Per-Agent Productivity (PAP).</span>
              )}
            </div>
          </div>
        }
        onChange={({ value }) => {
          dispatch(setPersonValue({ key: "agentStatus", value }));

          if (!editable && !organizationStartDate) {
            dispatch(
              setPersonValue({
                key: "milestones.organization_start_date",
                value: dayjs().format("YYYY-MM-DD"),
              }),
            );
          }
        }}
        data-cy="select_agent_status"
        menuShouldComeToFront
      />
      {!!person.agentStatus && person.agentStatus !== "Unlicensed" && (
        <>
          {person.agentStatus !== "Inactive" && (
            <FieldRadioV1
              label="Full / Part Time"
              value={person.agentHours}
              defaultValue="Full Time"
              options={agentHoursOptions}
              onChange={(value) => dispatch(setPersonValue({ key: "agentHours", value }))}
            />
          )}
          <FieldRadioV1
            label="Individual Real Estate LLC?"
            value={person.isRealEstateLlc ? "Yes" : "No"}
            options={["No", "Yes"]}
            onChange={(value) => {
              dispatch(setPersonValue({ key: "isRealEstateLlc", value: value === "Yes" }));
              if (value === "No") {
                dispatch(setPersonValue({ key: "individualRealEstateLlcName", value: null }));
              }
            }}
          />
          {person.isRealEstateLlc && (
            <FieldTextV1
              label="Real Estate LLC Name"
              value={person.individualRealEstateLlcName}
              onChange={(value) => dispatch(setPersonValue({ key: "individualRealEstateLlcName", value }))}
              required
            />
          )}
        </>
      )}
      <div className="tw-flex tw-gap-[24px]">
        <div className="">
          <FieldDateV1
            label="Start Date at Team"
            required
            value={organizationStartDate}
            onChange={(value) =>
              dispatch(setPersonValue({ key: "milestones.organization_start_date", value }))
            }
          />
          <div className={`${inputDescription} !tw-mt-[4px]`}>
            A start date is required to count towards the team’s Per-Agent Productivity (PAP)
          </div>
        </div>

        {!!person.agentStatus && person.agentStatus !== "Unlicensed" && (
          <FieldDateV1
            label="Licensed Since"
            value={licensedSince}
            onChange={(value) => dispatch(setPersonValue({ key: "milestones.licensed_since", value }))}
          />
        )}
      </div>
      {!!person.agentStatus && person.agentStatus !== "Unlicensed" && (
        <>
          <FieldTextV1
            label="Contract Agreement Link"
            placeholder="Paste contract agreement URL"
            value={person.contractAgreementLink}
            onChange={(value) => dispatch(setPersonValue({ key: "contractAgreementLink", value }))}
          />
          <Dropdown
            label="Brokerage Before Joining the Team"
            options={brokerageOptions}
            value={brokerageOptions.find((o) => o.value === (person.previousBrokerageName || "None"))}
            onChange={(o) => dispatch(setPersonValue({ key: "previousBrokerageName", value: o.value }))}
            menuShouldComeToFront
          />
          <AddMLSSection />
          <LicenseNumber />
          <FieldSearchLabelsV1
            label="Service Areas"
            placeholder="Search by city or ZIP / postal code"
            options={serviceAreaOptions}
            value={person.serviceAreas}
            searchText={serviceAreaInput}
            setSearchText={setServiceAreaInput}
            onChange={(value) => {
              const tempServiceAreas =
                !person.serviceAreas || person.serviceAreas.length <= 0
                  ? [value]
                  : [...person.serviceAreas, value];

              dispatch(setPersonValue({ key: "serviceAreas", value: [...tempServiceAreas] }));
            }}
            onRemove={(target) =>
              dispatch(
                setPersonValue({
                  key: "serviceAreas",
                  value: person.serviceAreas.filter((item) => item !== target),
                }),
              )
            }
          />

          <FieldSelectLabelsWithCheckboxOptions
            label="Property Specializations"
            placeholder="Select property types"
            value={person.specializations}
            onChange={(value) => dispatch(setPersonValue({ key: "specializations", value: [...value] }))}
            options={specializationOptions}
            onRemove={(target) =>
              dispatch(
                setPersonValue(
                  "specializations",
                  person.specializations.filter((item) => item !== target),
                ),
              )
            }
          />
        </>
      )}

      <CertificationsAndDesignationsSection />
      <FieldSearchLabelsV1
        label="Additional Fluent Languages"
        options={fluenciesOptions}
        value={person.fluencies}
        searchText={fluentLanguageInput}
        setSearchText={setFluentLanguageInput}
        onChange={(value) => {
          const tempFluencies =
            !person.fluencies || person.fluencies.length <= 0 ? [value] : [...person.fluencies, value].sort();

          dispatch(setPersonValue({ key: "fluencies", value: [...tempFluencies] }));
        }}
        onRemove={(target) =>
          dispatch(
            setPersonValue({
              key: "fluencies",
              value: person.fluencies.filter((item) => item !== target),
            }),
          )
        }
      />
      {!isPlaceBrand && (
        <>
          {person.sponsorName ? (
            <div className="tw-flex tw-gap-[24px]">
              <FieldTextV1
                label="Sponsor Name"
                toolTipInfoContent={<div>Who referred this user to your company.</div>}
                disabled
                value={person.sponsorName}
              />
              <FieldEmailV1
                label="Sponsor Email"
                value={person.sponsorEmail}
                onChange={(value) => dispatch(setPersonValue({ key: "sponsorEmail", value }))}
              />
            </div>
          ) : (
            <Button
              className="tw-w-fit tw-flex tw-items-center"
              onClick={() => setIsAddingSponsor(true)}
              disabled={!currentUser.account.isAdmin}
            >
              <Plus className="tw-mr-4px" size="s" />
              Sponsor
            </Button>
          )}
        </>
      )}

      {isAddingSponsor && (
        <AddSponsorModal show={isAddingSponsor} onClose={() => setIsAddingSponsor(false)} />
      )}
      {isAddingRevenuePartner && (
        <AddRevenuePartnerModal
          show={isAddingRevenuePartner}
          onClose={() => setIsAddingRevenuePartner(false)}
        />
      )}
      {isPlaceBrand && (
        <div className="tw-flex tw-items-center tw-gap-[12px]">
          <Toggle
            id="roster_toggle"
            checked={person.exportToRoster === "false"}
            onChange={() =>
              dispatch(
                setPersonValue({
                  key: "exportToRoster",
                  value: person.exportToRoster === "true" ? "false" : "true",
                }),
              )
            }
            text={null}
            data-cy="roster-toggle_field"
          />
          <FieldLabelV1
            {...{
              label: "Hide user from PLACE Roster",
              toolTipInfoContent: (
                <div className="tw-text-left tw-max-w-[220px]">
                  <p>Turn on if user is not a unique representation of a real person in the PLACE network.</p>
                  <p>
                    Examples include:
                    <ul className="tw-pl-[20px]">
                      <li>Duplicates of PLACE users who already exist in another CRM</li>
                      <li>Users which do not represent real people (e.g. &quot;Marketing Manager&quot;).</li>
                      <li>Note: this does not remove the user from your Brivity website.</li>
                    </ul>
                  </p>
                </div>
              ),
              toolTipPlacement: "right",
            }}
          />
        </div>
      )}
      {isPlaceBrand && (
        <>
          <hr className="tw-w-full tw-border-gray-10 tw-m-0" />
          <div className="tw-text-18d tw-text-neutral-gray-50">Revenue Partner</div>
          <div className="tw-text-gray-50">
            An agent’s revenue partner is the person formally designated on the Revenue Partnership Program
            Recruitment Form during onboarding. Adding a revenue partner can only be done once. They cannot be
            changed or removed once added.
          </div>
          {person.revenuePartnerType ? (
            <div className="tw-flex tw-gap-[24px]">
              <FieldTextV1
                label="Revenue Partner"
                toolTipInfoContent={
                  <div className="tw-text-left tw-text-12d">Who referred this user to PLACE.</div>
                }
                toolTipPlacement="top"
                readOnly
                value={
                  person.revenuePartnerType === "Person" ||
                  (!person.revenuePartnerType && person.revenuePartnerPersonName)
                    ? `${person.revenuePartnerPersonName}${
                        person.revenuePartnerPersonTeamName ? ` (${person.revenuePartnerPersonTeamName})` : ""
                      }`
                    : REV_TYPE_MAP_SRC[person.revenuePartnerType]
                }
              />
            </div>
          ) : (
            <Button
              className="tw-w-fit"
              onClick={() => setIsAddingRevenuePartner(true)}
              data-cy="add_revenue_partner_btn"
              disabled={!currentUser.account.isAdmin}
            >
              <Plus className="tw-mr-4px" size="s" />
              Revenue Partner
            </Button>
          )}
        </>
      )}
    </div>
  );
}

export default UserDetailsView;
